export type ViewType = 'video' | 'photos' | 'listing' | 'detail_page';

export type ViewSource =
	| 'reels'
	| 'search'
	| 'homepage'
	| 'curated_list'
	| 'property_page'
	| 'direct';

export interface ViewEngagement {
	duration: number;
	completionRate: number;
	clicked: boolean;
	favorited: boolean;
	contacted: boolean;
}

export interface ViewContext {
	position?: number;
}

export interface CreatePropertyViewPayload {
	propertyId: string;
	viewType: ViewType;
	source: ViewSource;
	sessionId: string;
	engagement?: Partial<ViewEngagement>;
	context?: ViewContext;
}

export interface UpdatePropertyViewPayload {
	engagement?: Partial<ViewEngagement>;
}

export interface PropertyViewResponse {
	_id: string;
	userId?: string;
	propertyId: string;
	viewType: ViewType;
	source: ViewSource;
	viewedAt: string;
	sessionId: string;
	engagement: ViewEngagement;
	context: ViewContext;
}

export interface OfflineVideoData {
	videoUrl: string;
	thumbnailUrl: string | null;
	propertyTitle: string;
	propertyCommercialId: string;
	// Additional property info for display
	region?: string;
	city?: string;
	askingPrice?: number | null;
	area?: number;
	bedrooms?: number | null;
	parkingSpots?: number | null;
	slug?: string;
}

export interface SyncOfflineViewRequest {
	propertyId: string;
	viewType: ViewType;
	source: ViewSource;
	sessionId?: string;
	engagement?: Partial<ViewEngagement>;
	context?: ViewContext;
	videoData?: OfflineVideoData;
	timestamp?: number; // When the view was last updated
}

export interface SyncOfflineViewsPayload {
	views: SyncOfflineViewRequest[];
}

export interface SyncOfflineViewsResponse {
	syncedCount: number;
	skippedCount: number;
}

export interface StartTrackingOptions {
	propertyId: string;
	viewType: ViewType;
	source: ViewSource;
	position?: number;
	videoDuration?: number;
	existingViewId?: string;
	videoData?: OfflineVideoData;
}

export interface TrackingState {
	viewId: string | null;
	propertyId: string;
	viewType: ViewType;
	source: ViewSource;
	startTime: number;
	videoDuration: number;
	currentTime: number;
	hasClicked: boolean;
	hasFavorited: boolean;
	hasContacted: boolean;
	position?: number;
	isEnded: boolean;
	videoData?: OfflineVideoData;
}

const DEFAULT_ENGAGEMENT: ViewEngagement = {
	duration: 0,
	completionRate: 0,
	clicked: false,
	favorited: false,
	contacted: false
};

export function createInitialState(
	options: StartTrackingOptions
): TrackingState {
	return {
		viewId: options.existingViewId ?? null,
		propertyId: options.propertyId,
		viewType: options.viewType,
		source: options.source,
		startTime: Date.now(),
		videoDuration: options.videoDuration ?? 0,
		currentTime: 0,
		hasClicked: false,
		hasFavorited: false,
		hasContacted: false,
		position: options.position,
		isEnded: false,
		videoData: options.videoData
	};
}

export function createViewPayload(
	options: StartTrackingOptions,
	sessionId: string
): CreatePropertyViewPayload {
	return {
		propertyId: options.propertyId,
		viewType: options.viewType,
		source: options.source,
		sessionId,
		// Don't send engagement on initial creation - it will be sent when there's actual engagement data
		// This prevents creating recentViews entries with 0 values
		...(options.position !== undefined && {
			context: { position: options.position }
		})
	};
}
